import { useEffect, useState } from 'react';
import { Check, X, PencilLine } from 'lucide-react';
import { trpc } from '@/data-loaders';
import { IconComponent } from '@/components';
import { Textarea } from '@/components/ui';
import { Button } from '@curtin-dxp/curtin-ui';
import { useHandleError } from '@/hooks';

interface NoteProps {
  schoolId: number;
  academicId: number;
  academicWorkloadNote: string;
}
export const Note = ({ schoolId, academicId, academicWorkloadNote }: NoteProps) => {
  const handleError = useHandleError();
  const [isNoteEditable, setIsNoteEditable] = useState(false);
  const [note, setNote] = useState<string>('');
  const trpcUtils = trpc.useUtils();

  const handleInvalidateAndRefetch = async () => {
    await trpcUtils.academic.getAcademicWorkloadAllocations.invalidate({ schoolId, academicId });
    await trpcUtils.academic.getAcademicWorkloadAllocations.refetch({ schoolId, academicId });
  };

  const { isLoading: isUpdating, mutate: updateNoteMutation } = trpc.academic.updateAcademicWorkloadNote.useMutation({
    onSuccess: async () => {
      await handleInvalidateAndRefetch();
    },
    onError: (error) => {
      handleError([error]);
    },
  });

  useEffect(() => {
    setNote(academicWorkloadNote);
  }, [academicWorkloadNote]);

  return (
    <div className="flex w-full flex-shrink-0 flex-col items-start gap-3 bg-primary-website-background-colour py-5 text-primary-black-90">
      <div className="flex items-center gap-2">
        <h4 className="font-proxima text-b3 font-bold">Add or edit a staff note</h4>
        {isNoteEditable ? (
          <div className="flex flex-row gap-1">
            <Button
              size="icon"
              variant="filled-secondary"
              className="h-8 w-8"
              onClick={() => {
                setIsNoteEditable(false);
                setNote(academicWorkloadNote);
              }}
            >
              <IconComponent IconType={X} />
            </Button>{' '}
            <Button
              size="icon"
              variant="filled-secondary"
              className="h-8 w-8"
              onClick={() => {
                setIsNoteEditable(false);
                updateNoteMutation({ academicId, note });
              }}
            >
              <IconComponent IconType={Check} />
            </Button>
          </div>
        ) : (
          <Button
            size="icon"
            variant="filled-secondary"
            className={`h-8 w-8 ${isUpdating ? 'cursor-wait' : 'cursor-pointer'}`}
            onClick={() => {
              setIsNoteEditable(true);
            }}
          >
            <IconComponent IconType={PencilLine} />
          </Button>
        )}
      </div>

      <Textarea
        className="h-[250px] w-full flex-shrink-0 rounded-lg border-secondary-grey-03 bg-primary-website-background-colour text-[12px]
          leading-6 text-black"
        maxLength={5000}
        disabled={!isNoteEditable}
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
        }}
      ></Textarea>
      {isNoteEditable && <div className="self-end text-xs">{5000 - (note?.length ?? 0)} characters remaining.</div>}
    </div>
  );
};
